import React, { useState, useCallback } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useRouter } from 'next/router';
import { useMutation, useApolloClient } from '@apollo/client';
import ValidationError from '@fuww/library/src/ValidationError';
import loginWithTokenMutation from '../../lib/mutations/loginWithToken.graphql';
import registerWithTokenMutation
  from '../../lib/mutations/registerWithToken.graphql';
import SocialLoginButton from './SocialLoginButton';
import { AnalyticsEventType, Registrar } from '../../lib/graphql/api/graphql';
import getArticleSubjectType from '../../lib/getArticleSubjectType';
import createAnalyticsEventMutation
  from '../../lib/mutations/createAnalyticsEvent.graphql';
import { useSiteContext } from '../SiteContext';

const loginWithProviderFormPropertyTypes = {
  provider: PropTypes.oneOf(['google', 'linkedin'] as const).isRequired,
  onClick: PropTypes.func,
};

type LoginWithProviderFormProperties = InferProps<
  typeof loginWithProviderFormPropertyTypes
>
  & { onClick?: () => void };

const LoginWithProviderForm = ({
  provider,
  onClick = () => {},
}: LoginWithProviderFormProperties) => {
  const { query: { id } } = useRouter();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [disabled, setDisabled] = useState(false);
  const client = useApolloClient();
  const {
    prefix,
    hasLocalNews,
    url,
    newsboard,
  } = useSiteContext();

  const onError = (error: Error) => {
    setDisabled(false);
    setErrorMessage(error.message);
  };

  const [
    registerUserWithToken,
  ] = useMutation(registerWithTokenMutation, {
    onError,
  });

  const [createAnalyticsEvent] = useMutation(createAnalyticsEventMutation);

  const [
    createSession,
  ] = useMutation(loginWithTokenMutation, {
    onCompleted: (data) => {
      setDisabled(false);
      localStorage.setItem('accessToken', data.loginWithToken.accessToken);
      window.plausible('Login', {
        props: {
          provider,
          source: 'LoginPage',
        },
      });

      createAnalyticsEvent({
        variables: {
          title: 'login',
          type: AnalyticsEventType.EVENT,
          url,
          payload: JSON.stringify({ newsboard }),
          subjectType: id ? getArticleSubjectType(prefix, hasLocalNews) : null,
          subjectId: id ? `${id}` : null,
        },
      });

      client.resetStore();
    },
    onError,
  });

  const onLogin = useCallback(() => {
    setDisabled(true);
    onClick();
  }, [setDisabled, onClick]);

  const onLoginSuccess = useCallback(async ({
    accessToken,
    providerArgument,
    registeredVia,
  }: {
    accessToken?: string;
    providerArgument: string;
    registeredVia: Registrar;
  }) => {
    const result = await registerUserWithToken({
      variables: { accessToken, provider: providerArgument, registeredVia },
    });
    if (result.data) {
      await createSession({
        variables: { accessToken, provider: providerArgument },
      });
    }
  }, [registerUserWithToken, createSession]);

  return (
    <>
      <SocialLoginButton
        labelKey="login.logInWithProvider"
        provider={provider}
        onLogin={onLogin}
        onLoginSuccess={onLoginSuccess}
        setErrorMessage={setErrorMessage}
        setDisabled={setDisabled}
        disabled={disabled}
      />
      { errorMessage ? (
        <ValidationError>
          { errorMessage }
        </ValidationError>
      ) : null}
    </>
  );
};

LoginWithProviderForm.propTypes = loginWithProviderFormPropertyTypes;

export default LoginWithProviderForm;
